// ./src/components/features/Venues/VenueAddressesEditor/VenueAddressesEditor.tsx
//
// Venue address editing pieces:
//   • VenueAddressFields  — the shared field layout for ONE address (street
//     autocomplete + parsed fields). No remove button, no primary toggle — the
//     drawer decides primary vs additional. Reused by the required "Primary
//     address" section and by each row of the repeatable list below.
//   • VenueAddressesEditor — the repeatable list of ADDITIONAL (non-primary)
//     addresses, with add/remove.
// The app has no generic array editor, so the add/remove-row pattern is composed
// from Mantine primitives (see em-scaffold-ui).

import React, { useCallback } from "react";
import { Stack, Group, Paper, TextInput, ActionIcon, Tooltip, Text, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Venue, VenueAddress, VenueAddressInput } from "@/utils/api";
import { AddressData } from "@/hooks";
import { AppIcon, AddressAutocompleteInput } from "@/components/generic";

// ── Row form model ──────────────────────────────────────────────────────────

export interface VenueAddressForm {
  /** Stable local key for React lists (NOT sent to the server). */
  key: string;
  /** Existing address id — absent for a newly added row. */
  id?: string;
  /** Preserved from load; the server owns this flag on save (see the drawer). */
  isPrimary: boolean;
  label: string;
  phone: string;
  contactName: string;
  streetAddress: string;
  extraAddress: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

// Monotonic local key generator (rows need a stable key before they have a
// server id). Module-scoped so keys stay unique across a session.
let keySeq = 0;
const nextKey = (): string => {
  keySeq += 1;

  return `addr-${keySeq}`;
};

export const createEmptyVenueAddress = (): VenueAddressForm => ({
  key: nextKey(),
  isPrimary: false,
  label: "",
  phone: "",
  contactName: "",
  streetAddress: "",
  extraAddress: "",
  city: "",
  stateProvince: "",
  postalCode: "",
  country: "",
  latitude: null,
  longitude: null,
});

const addressToForm = (a: VenueAddress): VenueAddressForm => ({
  key: nextKey(),
  id: a.id,
  isPrimary: !!a.isPrimary,
  label: a.label || "",
  phone: a.phone || "",
  contactName: a.contactName || "",
  streetAddress: a.streetAddress || "",
  extraAddress: a.extraAddress || "",
  city: a.city || "",
  stateProvince: a.stateProvince || "",
  postalCode: a.postalCode || "",
  country: a.country || "",
  latitude: typeof a.latitude === "number" ? a.latitude : null,
  longitude: typeof a.longitude === "number" ? a.longitude : null,
});

/** Build editor rows from a loaded venue (ordered as the API returned them). */
export const venueAddressesFromRecord = (record: Venue | null | undefined): VenueAddressForm[] =>
  (record?.addresses ?? []).map(addressToForm);

/**
 * Split a venue's addresses into { primary, additional } for the two-section
 * editor. When `extractPrimary` (a place-hosting venue, or a vendor with an
 * office slot) the primary is the isPrimary-flagged address — or the first,
 * mirroring the server's primaryAddress; otherwise there is no primary slot and
 * every address is additional.
 */
export const splitVenueAddresses = (
  record: Venue | null | undefined,
  extractPrimary: boolean
): { primary: VenueAddressForm; additional: VenueAddressForm[] } => {
  const all = venueAddressesFromRecord(record);

  if (!extractPrimary || all.length === 0) {
    return { primary: createEmptyVenueAddress(), additional: extractPrimary ? [] : all };
  }

  const flaggedIndex = all.findIndex((a) => a.isPrimary);
  const primaryIndex = flaggedIndex >= 0 ? flaggedIndex : 0;

  return {
    primary: all[primaryIndex],
    additional: all.filter((_, i) => i !== primaryIndex),
  };
};

/** A row is empty (nothing worth saving) when it has no street, city, or label. */
export const isVenueAddressEmpty = (a: VenueAddressForm): boolean =>
  !a.streetAddress.trim() && !a.city.trim() && !a.label.trim();

/**
 * Merge a Google AddressData (from a street or place lookup) into an address
 * form, keeping the row's key/id/label. Address fields are taken from the lookup,
 * falling back to the current value only where the lookup has nothing.
 */
export const applyAddressDataToForm = (form: VenueAddressForm, data: AddressData): VenueAddressForm => ({
  ...form,
  streetAddress: data.address1 ?? form.streetAddress,
  extraAddress: data.address2 ?? form.extraAddress,
  city: data.city ?? form.city,
  stateProvince: data.region ?? form.stateProvince,
  postalCode: data.postalCode ?? form.postalCode,
  country: data.country ?? form.country,
  latitude: typeof data.latitude === "number" ? data.latitude : form.latitude,
  longitude: typeof data.longitude === "number" ? data.longitude : form.longitude,
});

/** Map one editor row to a VenueAddressInput. `isPrimary`/`orderNo` are set server-side. */
export const venueAddressToInput = (a: VenueAddressForm): VenueAddressInput => ({
  id: a.id,
  label: a.label.trim() || null,
  phone: a.phone.trim() || null,
  contactName: a.contactName.trim() || null,
  streetAddress: a.streetAddress.trim() || null,
  extraAddress: a.extraAddress.trim() || null,
  city: a.city.trim() || null,
  stateProvince: a.stateProvince.trim() || null,
  postalCode: a.postalCode.trim() || null,
  country: a.country.trim() || null,
  latitude: a.latitude,
  longitude: a.longitude,
});

/** Map additional rows to VenueInput.addresses, dropping blank rows. */
export const venueAddressesToInput = (addresses: VenueAddressForm[]): VenueAddressInput[] =>
  addresses.filter((a) => !isVenueAddressEmpty(a)).map(venueAddressToInput);

// ── Shared single-address fields ─────────────────────────────────────────────

export interface VenueAddressFieldsProps {
  value: VenueAddressForm;
  onChange: (next: VenueAddressForm) => void;
  disabled?: boolean;
  /** Show the "Label" field. Off for the primary address (the venue name names it). */
  showLabel?: boolean;
}

export const VenueAddressFields: React.FC<VenueAddressFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  showLabel = true,
}) => {
  const { t } = useTranslation(["venues", "common"]);

  const patch = useCallback(
    (p: Partial<VenueAddressForm>) => {
      onChange({ ...value, ...p });
    },
    [value, onChange]
  );

  const handleAddressChange = useCallback(
    (address1: string, full: AddressData | null) => {
      if (!full) {
        // Plain typing — track the raw text, don't clobber the parsed fields.
        patch({ streetAddress: address1 });

        return;
      }

      const next: Partial<VenueAddressForm> = { streetAddress: full.address1 ?? address1 };

      if (full.address2) {
        next.extraAddress = full.address2;
      }
      if (full.city) {
        next.city = full.city;
      }
      if (full.region) {
        next.stateProvince = full.region;
      }
      if (full.postalCode) {
        next.postalCode = full.postalCode;
      }
      if (full.country) {
        next.country = full.country;
      }
      next.latitude = typeof full.latitude === "number" ? full.latitude : null;
      next.longitude = typeof full.longitude === "number" ? full.longitude : null;

      patch(next);
    },
    [patch]
  );

  return (
    <Stack gap="sm">
      {showLabel && (
        <TextInput
          label={t("venues:form.addresses.fields.label.label", { defaultValue: "Label" })}
          placeholder={t("venues:form.addresses.fields.label.placeholder", {
            defaultValue: "e.g. Main hall, Downtown branch",
          })}
          value={value.label}
          onChange={(e) => patch({ label: e.currentTarget.value })}
          disabled={disabled}
          size="md"
        />
      )}

      <AddressAutocompleteInput
        label={t("venues:form.addresses.fields.street.label", { defaultValue: "Street address" })}
        value={value.streetAddress}
        onChange={handleAddressChange}
        disabled={disabled}
        withinPortal
        size="md"
      />

      <TextInput
        label={t("venues:form.addresses.fields.extra.label", { defaultValue: "Apt / suite / floor" })}
        value={value.extraAddress}
        onChange={(e) => patch({ extraAddress: e.currentTarget.value })}
        disabled={disabled}
        size="md"
      />

      <Group grow align="flex-start">
        <TextInput
          label={t("venues:form.addresses.fields.city.label", { defaultValue: "City" })}
          value={value.city}
          onChange={(e) => patch({ city: e.currentTarget.value })}
          disabled={disabled}
          size="md"
        />
        <TextInput
          label={t("venues:form.addresses.fields.stateProvince.label", { defaultValue: "State / province" })}
          value={value.stateProvince}
          onChange={(e) => patch({ stateProvince: e.currentTarget.value })}
          disabled={disabled}
          size="md"
        />
      </Group>

      <Group grow align="flex-start">
        <TextInput
          label={t("venues:form.addresses.fields.postalCode.label", { defaultValue: "Postal code" })}
          value={value.postalCode}
          onChange={(e) => patch({ postalCode: e.currentTarget.value })}
          disabled={disabled}
          size="md"
        />
        <TextInput
          label={t("venues:form.addresses.fields.country.label", { defaultValue: "Country" })}
          value={value.country}
          onChange={(e) => patch({ country: e.currentTarget.value })}
          disabled={disabled}
          size="md"
        />
      </Group>

      <Group grow align="flex-start">
        <TextInput
          label={t("venues:form.addresses.fields.phone.label", { defaultValue: "Phone" })}
          value={value.phone}
          onChange={(e) => patch({ phone: e.currentTarget.value })}
          disabled={disabled}
          size="md"
        />
        <TextInput
          label={t("venues:form.addresses.fields.contactName.label", { defaultValue: "Contact person" })}
          value={value.contactName}
          onChange={(e) => patch({ contactName: e.currentTarget.value })}
          disabled={disabled}
          size="md"
        />
      </Group>
    </Stack>
  );
};

// ── Repeatable additional-addresses editor ───────────────────────────────────

export interface VenueAddressesEditorProps {
  value: VenueAddressForm[];
  onChange: (addresses: VenueAddressForm[]) => void;
  disabled?: boolean;
}

export const VenueAddressesEditor: React.FC<VenueAddressesEditorProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation(["venues", "common"]);

  const updateRow = useCallback(
    (index: number, next: VenueAddressForm) => {
      onChange(value.map((row, i) => (i === index ? next : row)));
    },
    [value, onChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const handleAdd = useCallback(() => {
    onChange([...value, createEmptyVenueAddress()]);
  }, [value, onChange]);

  return (
    <Stack gap="sm">
      {value.length === 0 && (
        <Text size="sm" c="dimmed">
          {t("venues:form.addresses.additionalEmpty", {
            defaultValue: "No additional addresses. Add one for a branch or extra location.",
          })}
        </Text>
      )}

      {value.map((row, index) => (
        <Paper key={row.key} withBorder radius="md" p="md">
          <Stack gap="sm">
            <Group justify="flex-end">
              <Tooltip label={t("venues:form.addresses.remove", { defaultValue: "Remove address" })} withArrow>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  onClick={() => handleRemove(index)}
                  disabled={disabled}
                  aria-label={t("venues:form.addresses.remove", { defaultValue: "Remove address" })}
                >
                  <AppIcon iconName="Trash" size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <VenueAddressFields value={row} onChange={(next) => updateRow(index, next)} disabled={disabled} />
          </Stack>
        </Paper>
      ))}

      <Box>
        <Button
          variant="light"
          size="md"
          onClick={handleAdd}
          disabled={disabled}
          leftSection={<AppIcon iconName="Plus" size={16} />}
        >
          {t("venues:form.addresses.add", { defaultValue: "Add address" })}
        </Button>
      </Box>
    </Stack>
  );
};
